import React, { lazy, Suspense } from "react";
import { Link } from "@/lib/router-compat";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import EsNavbar from "@/components/EsNavbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CLINIC_REF } from "@/components/schemaConstants";
import ClinicalReviewNote from "@/components/ClinicalReviewNote";
import { RelatedPages } from "@/components/RelatedPages";

// Reuse the existing tracking-backed verification form so GA4 generate_lead,
// the Google Ads conversion, Meta Lead, and the CRM custom-field mapping all
// fire exactly as on the English insurer pages. Form labels stay in English
// for this batch; the surrounding page chrome and body copy are fully Spanish.
const InsuranceVerificationForm = lazy(
  () => import("@/components/InsuranceVerificationForm"),
);
const CrisisLifelineBanner = lazy(
  () => import("@/components/CrisisLifelineBanner"),
);
const EsFooter = lazy(() => import("@/components/sections/EsFooter"));

export interface EsInsuranceFAQ {
  question: string;
  answer: string;
}

export interface EsInsuranceConfig {
  providerName: string;
  shortName: string;
  /** English slug (without /es/), e.g. "aetna-insurance" */
  slug: string;
  seoTitle: string;
  seoDescription: string;
  planType?: string;
  planRegion?: string;
  memberTerm?: string;
  planSpecificIntro?: string;
  acceleratedDescription?: string;
  faqs?: EsInsuranceFAQ[];
  officialResourceUrl?: string;
  officialResourceLabel?: string;
  reviewedBy?: string;
  lastUpdated?: string;
}

const PHONE = "954-827-9793";

const defaultEsFAQs = (shortName: string): EsInsuranceFAQ[] => [
  {
    question: `¿Cubre ${shortName} la TMS?`,
    answer: `La cobertura varía según el plan individual. Los ${shortName} miembros pueden tener beneficios que pueden aplicarse a la terapia TMS, pero la aprobación depende de su póliza específica, su diagnóstico y la documentación de necesidad médica. Nuestro equipo puede ayudarle a verificar sus beneficios y explicarle los próximos pasos.`,
  },
  {
    question: "¿Afecta mi diagnóstico a la cobertura?",
    answer:
      "Sí. Las aseguradoras pueden tener requisitos específicos de necesidad médica y elegibilidad para la TMS y otros tratamientos de neuromodulación. Suele requerirse un diagnóstico confirmado de depresión resistente al tratamiento, junto con la documentación de ensayos previos de antidepresivos.",
  },
  {
    question: "¿Necesito autorización previa?",
    answer: `Algunos planes pueden requerir autorización previa para la TMS y los protocolos de TMS acelerada. Esto se verificará según su plan individual. Nuestro equipo gestiona el proceso de autorización previa en su nombre, incluida la presentación de la documentación clínica y la gestión de apelaciones si fuera necesario.`,
  },
  {
    question: "¿Necesito haber probado medicación primero?",
    answer: `Algunos planes de seguro tienen requisitos de historial de tratamiento, que normalmente exigen documentación de ensayos adecuados de dos o más antidepresivos de distintas clases. Nuestro equipo puede revisar su situación y explicar qué información podría necesitarse para su plan de ${shortName}.`,
  },
  {
    question: "¿Puedo recibir SAINT a través de mi seguro?",
    answer: `La elegibilidad para SAINT y la cobertura del seguro dependen del plan específico y de las circunstancias clínicas. SAINT se factura bajo códigos CPT de Categoría III, y la cobertura aún se está desarrollando en la industria. Nuestro equipo puede ayudarle a determinar qué opciones podrían estar disponibles a través de su plan de ${shortName}.`,
  },
  {
    question: `¿Aceptan mi plan específico de ${shortName}?`,
    answer:
      "La participación del plan y los beneficios pueden variar incluso dentro de la misma aseguradora. Envíe su información de seguro para que nuestro equipo pueda verificar su plan específico, incluido el estado de la red, los copagos, los deducibles y la cobertura de TMS y servicios relacionados.",
  },
  {
    question: "¿Tengo que viajar a Florida?",
    answer:
      "Neuro Wellness TMS Centers of America está ubicado en el sur de Florida. Algunos pacientes viajan a nuestro centro para recibir tratamiento, especialmente para protocolos acelerados como SAINT que pueden completarse en una semana. Nuestro equipo puede hablar sobre la logística, los arreglos de viaje y las opciones disponibles.",
  },
  {
    question: "¿Cómo empiezo?",
    answer: `Complete el formulario de verificación de seguro en esta página o llámenos al (954) 827-9793. Nuestro equipo revisará sus beneficios de ${shortName}, hablará sobre su historial de tratamiento y le ayudará a entender qué opciones podrían estar disponibles.`,
  },
];

export function buildEsInsuranceSchema(config: EsInsuranceConfig): object[] {
  const { shortName, seoDescription, faqs } = config;
  const usingCustomFAQs = !!faqs;
  const faqItems = faqs || defaultEsFAQs(shortName);
  const blocks: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      inLanguage: "es-US",
      name: `Cobertura de ${shortName} para TMS y tratamiento de salud mental`,
      description: seoDescription,
      about: [
        "TMS Therapy",
        "SAINT TMS",
        "Depression Treatment",
        shortName,
        "Insurance Coverage",
      ],
      provider: CLINIC_REF,
    },
  ];
  if (usingCustomFAQs) {
    blocks.unshift({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: "es-US",
      mainEntity: faqItems.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
  }
  return blocks;
}

const EsInsurancePage: React.FC<{ config: EsInsuranceConfig }> = ({
  config,
}) => {
  const {
    providerName,
    shortName,
    acceleratedDescription,
    planSpecificIntro,
    planType,
    planRegion,
    memberTerm = "miembros",
    faqs,
    officialResourceUrl,
    officialResourceLabel,
    reviewedBy,
    lastUpdated,
  } = config;
  const faqItems = faqs || defaultEsFAQs(shortName);
  const memberCap = memberTerm.charAt(0).toUpperCase() + memberTerm.slice(1);

  return (
    <div className="min-h-screen bg-background">
      <EsNavbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero */}
        <section className="relative text-primary-foreground py-14 sm:py-24 overflow-hidden bg-primary">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary-deep/90" />
          <div className="container-px relative mx-auto max-w-5xl px-4">
            <div className="max-w-xl text-left space-y-5">
              <span className="inline-block rounded-full bg-accent/90 backdrop-blur-sm px-5 py-2 text-xs sm:text-sm font-bold text-white shadow-lg uppercase tracking-wider">
                Socio de seguro y cobertura
              </span>
              <h1 className="font-headings text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight drop-shadow-md">
                TMS y tratamiento de salud mental para {providerName}{" "}
                {memberCap}
              </h1>
              <p className="text-base sm:text-lg text-primary-foreground/90 leading-relaxed drop-shadow-md">
                ¿Tiene seguro de {shortName} y busca opciones de tratamiento
                para la depresión u otras preocupaciones de salud mental? Neuro
                Wellness TMS Centers of America puede ayudarle a entender sus
                opciones y determinar si nuestros servicios pueden estar
                disponibles a través de su plan específico.
              </p>
              <div className="flex flex-col sm:flex-row items-start justify-start gap-4 pt-3">
                <Button
                  asChild
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 text-base px-8 py-6"
                >
                  <a href="#verify-form">Verificar mis opciones de seguro</a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-white/60 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:border-white/80 text-base px-8 py-6"
                >
                  <a href="tel:9548279793">
                    <Phone className="h-5 w-5 mr-2" />
                    Llamar al 954-827-9793
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {(reviewedBy || lastUpdated) && (
          <div className="bg-background pt-6">
            <div className="container-px mx-auto max-w-4xl px-4">
              <ClinicalReviewNote
                reviewedBy={reviewedBy}
                lastUpdated={lastUpdated}
              />
            </div>
          </div>
        )}

        {/* Trust / value */}
        <section className="py-12 sm:py-16 bg-background">
          <div className="container-px mx-auto max-w-5xl">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Entender sus beneficios de seguro puede ser confuso. Nuestro
                equipo puede ayudarle a dar el siguiente paso revisando su
                información de seguro y ayudándole a entender qué opciones
                podrían estar disponibles.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  title: "Verificación de beneficios",
                  desc: "Revisamos los detalles de su plan para identificar la posible cobertura.",
                },
                {
                  title: "Orientación personalizada",
                  desc: "Nuestro equipo explica qué tratamientos podrían adaptarse a su situación.",
                },
                {
                  title: "Información sobre TMS y SAINT",
                  desc: "Conozca las opciones avanzadas de neuromodulación disponibles.",
                },
                {
                  title: "Programación de consulta",
                  desc: "Reserve una consulta telefónica gratuita en un horario que funcione para usted.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl border border-border bg-card p-5 shadow-soft space-y-3 text-center"
                >
                  <h3 className="font-headings text-base font-bold text-primary">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Does shortName cover TMS */}
        <section className="py-12 sm:py-16 bg-muted/30">
          <div className="container-px mx-auto max-w-4xl space-y-6">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-primary">
              ¿Cubre {shortName} la TMS?
            </h2>
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              {planSpecificIntro ? (
                <p>{planSpecificIntro}</p>
              ) : (
                <p>
                  {providerName} es{" "}
                  {planType
                    ? `un ${planType} `
                    : "un proveedor de seguro médico "}
                  {planRegion ? `que presta servicios en ${planRegion}. ` : ""}
                  Si su plan cubre la terapia TMS, los protocolos de TMS
                  acelerada u otros servicios de neuromodulación depende de
                  varios factores específicos de su póliza individual.
                </p>
              )}
              <p>
                Los {shortName} {memberTerm} pueden tener beneficios que
                aplicarse al tratamiento de salud mental. Sin embargo, la
                cobertura de TMS, los protocolos de TMS acelerada u otros
                servicios varía según el plan individual. Nuestro equipo puede
                ayudarle a verificar sus beneficios y explicarle los próximos
                pasos.
              </p>
              <p>Los factores que pueden afectar la cobertura incluyen:</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-1">
                {[
                  "Tipo de plan (PPO, HMO, EPO, Medicare Advantage, etc.)",
                  "Beneficios individuales y niveles de cobertura",
                  "Diagnóstico y necesidad médica documentada",
                  "Historial de tratamiento previo (ensayos de antidepresivos)",
                  "Estado de la red del proveedor de tratamiento",
                  "Requisitos de autorización previa",
                  "Disposiciones específicas de la póliza de seguro",
                  "Estado de emisión y regulaciones aplicables",
                ].map((factor) => (
                  <li key={factor} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{factor}</span>
                  </li>
                ))}
              </ul>
              <p>
                Le recomendamos enviar su información de seguro para que nuestro
                equipo pueda verificar su plan específico de {shortName} y
                ofrecerle una imagen clara de qué opciones podrían estar
                disponibles.
              </p>
            </div>
            <div className="pt-2">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#verify-form">Verificar mis opciones de seguro</a>
              </Button>
            </div>
          </div>
        </section>

        {/* What is TMS */}
        <section className="py-12 sm:py-16 bg-background">
          <div className="container-px mx-auto max-w-4xl space-y-6">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-primary">
              ¿Qué es la TMS?
            </h2>
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <p>
                La Estimulación Magnética Transcraneal (TMS) es un tratamiento
                no invasivo autorizado por la FDA que usa estimulación magnética
                para dirigirse a áreas específicas del cerebro implicadas en la
                regulación del estado de ánimo. Se usa habitualmente para
                ciertos pacientes con depresión que no han encontrado alivio
                suficiente con antidepresivos orales.
              </p>
              <p>Aspectos clave de la TMS:</p>
              <ul className="space-y-3">
                {[
                  "No invasiva: sin cirugía, sin implantes, sin anestesia para la TMS estándar",
                  "Usa pulsos magnéticos enfocados para estimular regiones cerebrales específicas",
                  "Se dirige a la corteza prefrontal dorsolateral, área implicada en la regulación del ánimo",
                  "Se usa habitualmente en adultos con depresión resistente al tratamiento",
                  "Se realiza de forma ambulatoria: usted se sienta en una silla y permanece despierto",
                  "Cada sesión suele durar unos 20–40 minutos",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                La TMS no es apropiada para todos. Si puede ser una opción para
                usted depende de su historial clínico, su medicación actual y
                otros factores que un profesional sanitario evalúa durante una
                consulta.
              </p>
            </div>
          </div>
        </section>

        {/* SAINT */}
        <section className="py-12 sm:py-16 bg-muted/30">
          <div className="container-px mx-auto max-w-4xl space-y-6">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-primary">
              Conozca SAINT TMS
            </h2>
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <p>
                Neuro Wellness TMS Centers of America ofrece SAINT (Stanford
                Accelerated Intelligent Neuromodulation Therapy), un protocolo
                de TMS acelerada para pacientes que cumplen los criterios. SAINT
                está autorizado por la FDA y puede implicar un calendario de
                tratamiento significativamente acelerado en comparación con la
                TMS convencional, comprimiendo lo que suele ser un ciclo de
                varias semanas en cinco días consecutivos.
              </p>
              <p>
                {acceleratedDescription ||
                  `SAINT puede ser una opción para ciertos pacientes con depresión. La elegibilidad depende de la evaluación clínica y las circunstancias individuales, incluido si la orientación por fMRI y un calendario acelerado son apropiados para su situación.`}
              </p>
              <p>
                SAINT no es apropiada para todos. Se requiere una evaluación
                clínica para determinar si este protocolo acelerado puede ser
                adecuado para usted, y la cobertura a través de {shortName}{" "}
                depende de su plan específico y de la documentación de necesidad
                médica.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <Link to="/es/saint">Conozca SAINT</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-primary/20 bg-transparent text-primary hover:bg-primary/5"
              >
                <a href="#verify-form">Verificar mis opciones de seguro</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Verification form */}
        <section
          id="verify-form"
          className="py-12 sm:py-16 bg-background scroll-mt-20"
        >
          <div className="container-px mx-auto max-w-3xl">
            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft">
              <div className="text-center space-y-3 mb-8">
                <h2 className="font-headings text-2xl sm:text-3xl font-bold text-primary">
                  Verifiquemos sus opciones
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                  Complete el formulario a continuación y nuestro equipo le
                  ayudará a entender qué opciones pueden estar disponibles a
                  través de su plan de {shortName}.
                </p>
              </div>
              <Suspense fallback={null}>
                <InsuranceVerificationForm
                  insuranceName={providerName}
                  variant="mid"
                  locale="es"
                />
              </Suspense>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 sm:py-16 bg-muted/30">
          <div className="container-px mx-auto max-w-3xl space-y-6">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-primary">
              Preguntas frecuentes sobre la cobertura de {shortName}
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((faq, idx) => (
                <AccordionItem
                  key={idx}
                  value={`item-${idx}`}
                  className="rounded-xl border border-border bg-card px-5 shadow-soft"
                >
                  <AccordionTrigger className="text-left text-base font-semibold text-primary hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative bg-gradient-navy text-primary-foreground py-16 sm:py-20 overflow-hidden">
          <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />
          <div className="container-px relative mx-auto max-w-4xl text-center space-y-6">
            <h2 className="font-headings text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white">
              ¿Listo para explorar sus opciones?
            </h2>
            <p className="text-base sm:text-lg text-primary-foreground/80 max-w-2xl mx-auto leading-relaxed">
              No intente averiguar sus beneficios de seguro solo. Envíe su
              información y deje que nuestro equipo le ayude a entender sus
              posibles opciones de tratamiento.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#verify-form">Verificar mis opciones de seguro</a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white/50 bg-transparent text-white hover:bg-white/10"
              >
                <a href="tel:9548279793">
                  <Phone className="h-4 w-4 mr-2" />
                  Llamar al (954) 827-9793
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Disclaimer + crisis */}
        <div className="container-px mx-auto max-w-4xl px-4 py-8 space-y-4">
          <p className="text-xs leading-relaxed text-muted-foreground">
            Neuro Wellness TMS Centers of America es una práctica psiquiátrica
            independiente y no está afiliada, respaldada ni patrocinada por{" "}
            {shortName}. {shortName} es una marca comercial de su respectivo
            propietario. La cobertura de TMS, SAINT® TMS y Spravato® varía según
            el plan y no está garantizada; nuestro equipo verifica sus
            beneficios específicos sin coste.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            La información de esta página es solo con fines educativos y no
            constituye consejo médico ni una determinación de cobertura. La
            cobertura de TMS, SAINT, SPRAVATO® y otros tratamientos varía según
            el plan individual y está sujeta a la revisión de necesidad médica
            por parte de {providerName}. La verificación de beneficios no es una
            garantía de pago. Siempre hable sobre las opciones de tratamiento y
            los resultados esperados con su médico o proveedor de salud mental
            cualificado.
          </p>
          <Suspense fallback={null}>
            <CrisisLifelineBanner className="mb-4" />
          </Suspense>
        </div>

        <RelatedPages
          heading="Recursos relacionados de seguro y tratamiento"
          links={[
            {
              title: "Cómo funciona el seguro aquí",
              href: "/how-insurance-works",
              description: "Verificación frente a autorización previa",
            },
            {
              title: "Resumen de costo y seguro",
              href: "/cost-insurance",
              description: "Todos los seguros aceptados y financiación",
            },
            {
              title: "SAINT® TMS acelerada",
              href: "/es/saint",
              description:
                "Protocolo de TMS acelerada de 5 días autorizado por la FDA",
            },
            {
              title: "Terapia TMS estándar",
              href: "/es/services/tms",
              description:
                "Estimulación magnética no invasiva para la depresión",
            },
            {
              title: "SPRAVATO® (esketamina)",
              href: "/es/spravato",
              description: "Aerosol nasal autorizado por la FDA para DRT",
            },
          ]}
        />
      </main>
      <Suspense fallback={null}>
        <EsFooter />
      </Suspense>
    </div>
  );
};

export default EsInsurancePage;
